/**
 * EduSense 테마를 ui-layout ThemeProvider 인터페이스로 구현
 * 통합된 테마 시스템(@edusense/shared-theme)을 활용하여 학습 친화적 테마 제공
 */
import { useState, useEffect } from 'react';
import type { ThemeProvider } from '@edusense/shared-ui-layout';
import { 
  lightTheme, 
  darkTheme, 
  createAppTheme, 
  edusenseAppColors, 
  edusenseAppDarkColors 
} from '@edusense/shared-theme';

/**
 * EduSense 테마를 ui-layout ThemeProvider 형식으로 변환하는 훅
 * 통합된 테마 시스템을 사용하여 학습 친화적 블루 테마 제공
 */
export const useUILayoutTheme = (): ThemeProvider => {
  const [isDark, setIsDark] = useState(false);

  // 시스템 테마 감지 및 초기화
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDark(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setIsDark(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // EduSense 앱별 테마 생성 (통합된 테마 시스템 사용)
  const edusenseLightTheme = createAppTheme(lightTheme, edusenseAppColors);
  const edusenseDarkTheme = createAppTheme(darkTheme, edusenseAppDarkColors);
  
  const currentTheme = isDark ? edusenseDarkTheme : edusenseLightTheme;
  
  return {
    background: currentTheme.background,
    paper: currentTheme.paper,
    paperLight: currentTheme.paperLight,
    paperHover: currentTheme.paperHover,
    paperActive: currentTheme.paperActive,
    text: currentTheme.text,
    textSecondary: currentTheme.textSecondary,
    border: currentTheme.border,
    primary: currentTheme.primary,
    secondary: currentTheme.secondary,
    breakpoints: {
      sm: '640px',
      md: '768px', 
      lg: '1024px',
      xl: '1280px',
    },
    spacing: {
      xs: '0.5rem',
      sm: '1rem',
      md: '1.5rem',
      lg: '2rem',
      xl: '3rem',
    },
    radius: {
      sm: '0.25rem',
      md: '0.375rem',
      lg: '0.5rem',
    },
  };
};